// Tap vs press-and-hold arbitration for the ONE existing MissionReady Voice
// button. Pure and timer-injectable so the lifecycle can be unit tested.
//
// Short tap  -> existing D.A.R. memo behaviour (unchanged).
// Long press -> MissionReady Radio transmission (existing live Radio layer).
//
// A single press resolves to exactly one outcome: never both, never twice.

/** Deliberate hold threshold: long enough that a normal tap can't become Radio. */
export const RADIO_HOLD_THRESHOLD_MS = 600;

export type VoiceGestureCallbacks = {
  /** Existing DAR tap behaviour. */
  onDarTap: () => void;
  /** Threshold reached — begin Radio. */
  onRadioStart: () => void;
  /** Release/cancel while Radio is engaged. */
  onRadioStop: () => void;
  /** Optional pressed/armed visual feedback. */
  onHoldChange?: (holding: boolean) => void;
};

type Timers = {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
};

export type VoiceGesture = {
  pointerDown: () => void;
  pointerUp: () => void;
  /** Pointer/touch cancellation, leave, unmount, interruption. */
  cancel: () => void;
  /** True while the press has escalated to Radio. */
  isRadioEngaged: () => boolean;
};

export function createVoiceGesture(
  callbacks: VoiceGestureCallbacks,
  options: { thresholdMs?: number; timers?: Timers } = {},
): VoiceGesture {
  const threshold = options.thresholdMs ?? RADIO_HOLD_THRESHOLD_MS;
  const timers: Timers = options.timers ?? {
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  };

  let handle: unknown = null;
  let pressing = false;
  let radio = false;

  const clear = () => {
    if (handle !== null) {
      timers.clearTimeout(handle);
      handle = null;
    }
  };

  const reset = () => {
    clear();
    pressing = false;
    radio = false;
    callbacks.onHoldChange?.(false);
  };

  return {
    pointerDown() {
      if (pressing) return;
      pressing = true;
      radio = false;
      callbacks.onHoldChange?.(true);
      handle = timers.setTimeout(() => {
        handle = null;
        if (!pressing) return;
        radio = true;
        callbacks.onRadioStart();
      }, threshold);
    },
    pointerUp() {
      if (!pressing) return;
      const wasRadio = radio;
      reset();
      // Radio press must never also fire DAR, and vice versa.
      if (wasRadio) callbacks.onRadioStop();
      else callbacks.onDarTap();
    },
    cancel() {
      if (!pressing) return;
      const wasRadio = radio;
      reset();
      if (wasRadio) callbacks.onRadioStop();
    },
    isRadioEngaged() {
      return radio;
    },
  };
}
